import * as React from 'react';
import styled from 'styled-components';
import {Field, FieldProps, Form, Formik} from 'formik';
import {transparentize} from 'polished';
import {OptionsType, OptionTypeBase} from 'react-select';
import {FileObject, FileType} from 'api/library';
import BoxContainer from 'containers/BoxContainer';
import {Container as InputContainer, Label} from 'containers/InputContainer';
import Input from 'components/Input';
import Button, {ButtonVariant} from 'components/Button';
import Select, {OptionObject} from 'components/Select';
import {Container as SocialButton, SocialType} from 'components/SocialButton';
import {InputWrapper} from 'components/InputSubmit';
import FilePicker from 'components/FilePicker';
import UserAvatar, {Wrapper as UserAvatarWrapper} from 'components/UserAvatar';
import SocialInput from 'components/SocialInput';
import RadioButton from 'components/RadioButton';
import {GenderType, ProfileUser} from '../types';
import {prepareProfileData} from '../helpers';
import {PreviewObject} from 'types';
import {DatePicker} from "components/DatePicker";
import {Theme} from '../../../stores/AppStore';

export interface ProfileEditProps {
  onSubmit: (data: ProfileUser) => void,
  user: ProfileUser,
}

const Wrapper = styled.div`
  margin-top: 50px;

  display: grid;
  gap: 10px;

  ${InputContainer} {
    gap: 10px;
  }

  ${Label} {
    font-size: 13px;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0.01em;
  }
`;

const Container = styled(BoxContainer)`
  margin: 0 auto;
  display: grid;
  gap: 20px;
  width: 100%;
  max-width: 930px;
  align-self: start;
  padding: 20px;
  border-width: 1px;
`;

const RadioWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 5px;
`;

const BoxTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.01em;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media(max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Main = styled.div`
  display: grid;
  gap: 20px;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 30px;
  
  @media(max-width: 560px) {
    grid-template-columns: 1fr;
    
    ${UserAvatarWrapper} {
      margin: auto;
    }
  }
`;

const Group = styled.div`
  display: grid;
  gap: 20px;
`;

const Socials = styled.div`
  display: grid;
  gap: 20px;
  
  ${SocialButton} {
    width: 20px;
    height: 20px;
  }
`;

const Textarea = styled.textarea`
  padding: 14px;
  background: ${({theme}) => theme.name === Theme.Dark ? '#232323' : theme.variables.backgroundColor};
  border-radius: 5px;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
`;

const FieldBox = styled.div`
  display: grid;
  gap: 10px;
`;

const FieldLabel = styled.div`
  font-size: 13px;
  line-height: 17px;
`;

const FieldDescription = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${({theme}) => `
    ${transparentize(.6, theme.colors.white)};
  `};
`;

const Footer = styled.div`
  padding: 13px;
  background: transparent;
  margin-top: 44px;
  box-shadow: 5px 5px 60px rgba(24, 24, 24, 0.8);
`;

const Buttons = styled.div`
  margin: 0 auto;
  max-width: 930px;
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 10px;
  justify-content: end;
`;

const SelectOptionLabel = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 10px;

  ${SocialButton} {
    width: 18px;
    height: 18px;
  }
`;

/**
 * The component render summary section
 * @param {ProfileUser} props
 * @return {React.ReactElement}
 * @constructor
 */
export default function ProfileEdit(
    props: ProfileEditProps,
): React.ReactElement {
  /**
   * The function render select option title
   * @param {string} title
   * @return {React.ReactElement}
   */
  function renderSelectTitle(title: string):JSX.Element {
    return <div>{title}</div>;
  }

  /**
   * The function return options for select of communication priority
   * @param {ProfileUser} values
   * @return {OptionsType}
   */
  function getSocialsSelectOptions(values: ProfileUser): OptionsType<any> {
    const options = [];

    if (values.socials?.telegram) {
      const {telegram} = values.socials;
      options.unshift({
        label: getSocialsSelectLabel(SocialType.Telegram, telegram),
        value: telegram,
      });
    }

    if (values.socials?.instagram) {
      const {instagram} = values.socials;

      options.unshift({
        label: getSocialsSelectLabel(SocialType.Instagram, instagram),
        value: instagram,
      });
    }

    if (values.socials?.vkontakte) {
      const {vkontakte} = values.socials;

      options.unshift({
        label: getSocialsSelectLabel(SocialType.Vkontakte, vkontakte),
        value: vkontakte,
      });
    }

    if (values?.contacts?.phone) {
      const {phone} = values.contacts;

      options.unshift({
        label: renderSelectTitle('Тел: ' + phone),
        value: phone,
      });
    }

    if (values?.contacts?.email) {
      const {email} = values.contacts;

      options.unshift({
        label: renderSelectTitle('Email: ' + email),
        value: email,
      });
    }

    return options;
  }

  /**
   * The function return select options of priority communication
   * @param {SocialType} type
   * @param {string} value
   * @return {React.ReactElement}
   */
  function getSocialsSelectLabel(type: SocialType, value: string) {
    return (
      <SelectOptionLabel>
        <SocialButton
          type={type}
          noEffect
        />
        <div>{value}</div>
      </SelectOptionLabel>
    );
  }

  /**
   * Handle change preferred communication method
   * @param {string} fieldName
   * @param {OptionObject} selected
   * @param {Function} cb
   */
  function onPreferredCommunicationChange(
      fieldName: string,
      selected: OptionTypeBase | null,
      cb: Function,
  ): void {
    if (selected?.value) {
      cb(fieldName, selected);
    }
  }

  /**
   * Handle form submit
   * @param {ProfileUser} values
   */
  function handleSubmit(values: ProfileUser) {
    props.onSubmit(values);
  }

  /**
   * set user avatar
   * @param {FileObject} preview
   * @param {Function} cb
   */
  function setAvatar(preview: FileObject, cb: Function) {
    const avatar: PreviewObject = {
      id: preview.id,
      type: preview.type,
      url: preview.url,
    };
    cb('avatar', avatar);
  }

  const [isAvatarLoad, setAvatarLoad] = React.useState(false);

  return (
    <Formik
      initialValues={prepareProfileData(props.user)}
      onSubmit={handleSubmit}
    >
      {({values, setFieldValue}) => (
        <Form>
          <Wrapper>
            <Container>
              <Main>
                <Header>
                  <UserAvatar
                    preview={values?.avatar}
                    onClickLoad={() => setAvatarLoad(true)}
                    onClickDelete={() => setFieldValue('avatar', null)}
                    editable
                  />
                  {isAvatarLoad && (
                    <FilePicker
                      upload
                      type={FileType.Image }
                      onBlur={() => setAvatarLoad(false)}
                      onSelect={(file) => {
                        setAvatar(file[0], setFieldValue);
                        setAvatarLoad(false);
                      }}
                    />
                  )}
                  <Group>
                    <Field name="name">
                      {({field}: FieldProps) => (
                        <InputWrapper>
                          <Input
                            type="text"
                            label="Имя"
                            placeholder="Имя"
                            required
                            {...field}
                          />
                        </InputWrapper>
                      )}
                    </Field>
                    <Field name="surname">
                      {({field}: FieldProps) => (
                        <InputWrapper>
                          <Input
                            type="text"
                            label="Фамилия"
                            placeholder="Фамилия"
                            {...field}
                          />
                        </InputWrapper>
                      )}
                    </Field>
                  </Group>
                </Header>
                <Field name="nickname">
                  {({field}: FieldProps) => (
                    <InputWrapper>
                      <Input
                        type="text"
                        label="Ник"
                        placeholder="Ник"
                        required
                        {...field}
                      />
                    </InputWrapper>
                  )}
                </Field>
                <Row>
                  <Field name="info.birthday">
                    {({field}: FieldProps) => (
                      <InputWrapper>
                        <DatePicker
                          label="Дата рождения"
                          id={field.name}
                          dateFormat="dd.MM.yyyy"
                          selected={field.value}
                          onChange={
                            (date) => setFieldValue(field.name, date)
                          }
                        />
                      </InputWrapper>
                    )}
                  </Field>
                  <Field name="info.city">
                    {({field}: FieldProps) => (
                      <InputWrapper>
                        <Input
                          type="text"
                          label="Город"
                          placeholder="Город"
                          {...field}
                        />
                      </InputWrapper>
                    )}
                  </Field>
                </Row>
                <Field name="gender">
                  {({field}: FieldProps) => (
                    <FieldBox>
                      <FieldLabel>Пол</FieldLabel>
                      <RadioWrapper>
                        <RadioButton
                          {...field}
                          label="Мужской"
                          defaultChecked={field.value === GenderType.Male}
                          onChange={(e) => {
                            e.target.value
                            && setFieldValue(field.name, GenderType.Male);
                          }}
                        />
                        <RadioButton
                          {...field}
                          label="Женский"
                          defaultChecked={field.value === GenderType.Female}
                          onChange={(e) => {
                            e.target.value
                            && setFieldValue(field.name, GenderType.Female);
                          }}
                        />
                      </RadioWrapper>
                    </FieldBox>
                  )}
                </Field>
                <FieldBox>
                  <FieldLabel>О себе</FieldLabel>
                  <Field name="info.about">
                    {({field}: FieldProps) => (
                      <InputWrapper>
                        <Textarea
                          rows={6}
                          placeholder="о себе"
                          {...field}
                        />
                      </InputWrapper>
                    )}
                  </Field>
                </FieldBox>
                <Field name="video">
                  {({field}: FieldProps) => (
                    <InputWrapper>
                      <Input
                        type="text"
                        label="Ссылка на видео-визитку (YouTube, Vimeo)"
                        placeholder="Ссылка на видео-визитку (YouTube, Vimeo)"
                        {...field}
                      />
                    </InputWrapper>
                  )}
                </Field>
              </Main>
            </Container>
            <Container>
              <BoxTitle>Контакты</BoxTitle>
              <Field name="contacts.phone">
                {({field}: FieldProps) => (
                  <InputWrapper>
                    <Input
                      type="text"
                      label="Телефон"
                      placeholder="+_ (___) __-__-___"
                      {...field}
                    />
                  </InputWrapper>
                )}
              </Field>
              <Field name="contacts.email">
                {({field}: FieldProps) => (
                  <InputWrapper>
                    <Input
                      type="email"
                      label="E-mail"
                      placeholder="E-mail"
                      {...field}
                    />
                  </InputWrapper>
                )}
              </Field>

              <Socials>
                <Field name="socials.telegram">
                  {({field}: FieldProps) => (
                    <InputWrapper>
                      <SocialInput
                        type={SocialType.Telegram}
                        {...field}
                      />
                    </InputWrapper>
                  )}
                </Field>
                <Field name="socials.instagram">
                  {({field}: FieldProps) => (
                    <InputWrapper>
                      <SocialInput
                        type={SocialType.Instagram}
                        {...field}
                      />
                    </InputWrapper>
                  )}
                </Field>
                <Field name="socials.vkontakte">
                  {({field}: FieldProps) => (
                    <InputWrapper>
                      <SocialInput
                        type={SocialType.Vkontakte}
                        {...field}
                      />
                    </InputWrapper>
                  )}
                </Field>
              </Socials>

              <FieldBox>
                <FieldLabel>Приоритетный способ связи</FieldLabel>
                <Field name="preferredCommunication">
                  {({field}: FieldProps) => (
                    <InputWrapper>
                      <Select
                        placeholder="Выбрать..."
                        onChange={
                          (value) => (
                            onPreferredCommunicationChange(
                                field.name,
                                value,
                                setFieldValue,
                            )
                          )
                        }
                        options={getSocialsSelectOptions(values)}
                        menuPlacement={'top'}
                      />
                    </InputWrapper>
                  )}
                </Field>
                <FieldDescription>
                Выберите из указанных
                Вами контактов приоритетный способ связи,
                который будут использовать другие пользователи.
                </FieldDescription>
              </FieldBox>
            </Container>
            <Footer>
              <Buttons>
                <Button
                  variant={ButtonVariant.Outline}
                >Отмена</Button>
                <Button
                  type="submit"
                  variant={ButtonVariant.Primary}
                >Сохранить</Button>
              </Buttons>
            </Footer>
          </Wrapper>
        </Form>
      )}
    </Formik>
  );
};
